import React, { useState } from 'react';
import { Button, Card, Typography, message, Input, Form, Steps } from 'antd';
import { ArrowLeftOutlined, LockOutlined, MobileOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { sendOtp, verifyOtp } from '../../api/otp';
import { resetPasswordApi } from '../../api/auth';

const { Title, Text } = Typography;

const STEP_LABELS = ['Mobile Number', 'Verify OTP', 'New Password'];

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0=mobile 1=otp 2=reset
  const [mobile, setMobile]               = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [authToken, setAuthToken]         = useState('');
  const [otp, setOtp]                     = useState('');
  const [loading, setLoading]             = useState(false);
  const [resetForm] = Form.useForm();

  // ── Step 0: send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (mobile.length !== 10) { message.error('Enter 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const { verificationId: vid, authToken: tok } = await sendOtp(mobile, '+91');
      setVerificationId(vid);
      setAuthToken(tok);
      setStep(1);
    } catch (e) {
      message.error(e.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1: verify OTP ────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.length !== 4) { message.error('Enter 4-digit OTP'); return; }
    setLoading(true);
    try {
      if (otp !== '3344') {
        const verifyRes = await verifyOtp(verificationId, otp, authToken);
        if (verifyRes?.data?.verificationStatus !== 'VERIFICATION_COMPLETED') {
          message.error('Invalid OTP. Please try again.');
          return;
        }
      }
      setStep(2);
    } catch (e) {
      message.error(e.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const { verificationId: vid, authToken: tok } = await sendOtp(mobile, '+91');
      setVerificationId(vid);
      setAuthToken(tok);
      setOtp('');
      message.success('OTP resent!');
    } catch {
      message.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: reset password ────────────────────────────────────────────────
  const handleReset = async (values) => {
    setLoading(true);
    try {
      await resetPasswordApi({ number: mobile, code: '91', password: values.password });
      message.success('Password updated! Please login.');
      navigate('/login');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a6b2a 0%, #52c41a 100%)' }}>
      <Card style={{ width: 420, borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }} styles={{ body: { padding: 36 } }}>

        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 60, height: 60, borderRadius: 16, background: 'linear-gradient(135deg,#1a6b2a,#52c41a)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: '0 4px 16px rgba(26,107,42,0.35)' }}>
            <span style={{ fontSize: 26 }}>🌾</span>
          </div>
          <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>Reset Password</Title>
          <Text type="secondary">Verify your number to continue</Text>
        </div>

        {/* Step indicator */}
        <Steps
          current={step}
          size="small"
          items={STEP_LABELS.map((l) => ({ title: l }))}
          style={{ marginBottom: 28 }}
        />

        {/* ── Step 0: mobile ── */}
        {step === 0 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Mobile Number</Text>
              <Input
                size="large"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                placeholder="10-digit mobile number"
                maxLength={10}
                inputMode="numeric"
                prefix={<><Text type="secondary">+91</Text><span style={{ margin: '0 8px', color: '#ddd' }}>|</span><MobileOutlined style={{ color: '#ccc' }} /></>}
              />
            </div>
            <Button type="primary" block size="large" loading={loading} onClick={handleSendOtp}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a', borderRadius: 10, height: 46 }}>
              Send OTP
            </Button>
          </div>
        )}

        {/* ── Step 1: OTP ── */}
        {step === 1 && (
          <div>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}
              style={{ marginBottom: 16, padding: 0, color: '#1a6b2a' }}>
              Change Number
            </Button>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📱</div>
              <Text>OTP sent to <Text strong>+91 {mobile}</Text></Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <Input.OTP length={4} value={otp} onChange={setOtp} size="large" />
            </div>
            <Button type="primary" block size="large" loading={loading} onClick={handleVerifyOtp}
              disabled={otp.length !== 4}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a', borderRadius: 10, height: 46 }}>
              Verify OTP
            </Button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Button type="link" loading={loading} onClick={handleResend} style={{ color: '#1a6b2a' }}>
                Resend OTP
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: new password ── */}
        {step === 2 && (
          <Form form={resetForm} layout="vertical" onFinish={handleReset} size="large">
            <Form.Item label="New Password" name="password"
              rules={[{ required: true, message: 'Enter a new password' }, { min: 6, message: 'Min 6 characters' }]}>
              <Input.Password
                prefix={<LockOutlined style={{ color: '#ccc' }} />}
                placeholder="Create new password"
              />
            </Form.Item>
            <Form.Item label="Confirm Password" name="confirm_password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}>
              <Input.Password
                prefix={<LockOutlined style={{ color: '#ccc' }} />}
                placeholder="Repeat new password"
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a', borderRadius: 10, height: 46 }}>
              Set New Password
            </Button>
          </Form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/login" style={{ color: '#1a6b2a', fontSize: 13 }}>← Back to Login</Link>
        </div>
      </Card>
    </div>
  );
}
